import ReactGA from "react-ga4";

/**
 * GA4 초기화 함수
 * @param {string} trackingId - GA4 추적 ID (G-XXXXXXXXXX)
 */
export const initializeGA4 = (trackingId) => {
    if (!trackingId) {
        console.warn("GA4 Tracking ID가 제공되지 않았습니다.");
        return;
    }

    try {
        ReactGA.initialize(trackingId, {
            debug: process.env.NODE_ENV === "development",
            gtagOptions: {
                send_page_view: false, // 수동으로 페이지뷰 관리
            },
        });
        console.log("GA4 초기화 완료:", trackingId);
    } catch (error) {
        console.error("GA4 초기화 실패:", error);
    }
};

/**
 * 페이지뷰 추적
 * @param {string} path - 페이지 경로
 * @param {string} title - 페이지 제목 (선택사항)
 */
export const trackPageView = (path, title) => {
    try {
        ReactGA.send({
            hitType: "pageview",
            page: path,
            title: title || document.title,
        });
        console.log("페이지뷰 추적:", path);
    } catch (error) {
        console.error("페이지뷰 추적 실패:", error);
    }
};

/**
 * 클릭 이벤트 추적
 * @param {Object} eventData - 이벤트 데이터
 * @param {string} eventData.action - 이벤트 액션 (예: 'click')
 * @param {string} eventData.category - 이벤트 카테고리 (예: 'class_card')
 * @param {string} eventData.label - 이벤트 라벨 (예: 클래스명)
 * @param {number} eventData.value - 이벤트 값 (선택사항)
 * @param {string} eventData.userId - 사용자 ID (선택사항)
 * @param {Object} eventData.customParameters - 추가 커스텀 파라미터
 */
export const trackClickEvent = ({
    action = "click",
    category,
    label,
    value,
    userId,
    customParameters = {},
}) => {
    if (!category || !label) {
        console.warn("GA4 클릭 이벤트: category와 label은 필수입니다.");
        return;
    }

    try {
        const eventParameters = {
            event_category: category,
            event_label: label,
            ...customParameters,
        };

        // 값이 있으면 추가
        if (value !== undefined && value !== null) {
            eventParameters.value = value;
        }

        // 사용자 ID가 있으면 추가
        if (userId) {
            eventParameters.user_id = userId;
        }

        ReactGA.event(action, eventParameters);
    } catch (error) {
        console.error("클릭 이벤트 추적 실패:", error);
    }
};

/**
 * 사용자 속성 설정
 * @param {Object} userProperties - 사용자 속성
 */
export const setUserProperties = (userProperties) => {
    try {
        ReactGA.set(userProperties);
        console.log("사용자 속성 설정:", userProperties);
    } catch (error) {
        console.error("사용자 속성 설정 실패:", error);
    }
};

/**
 * 커스텀 이벤트 추적 (일반적인 용도)
 * @param {string} eventName - 이벤트명
 * @param {Object} parameters - 이벤트 파라미터
 */
export const trackCustomEvent = (eventName, parameters = {}) => {
    try {
        ReactGA.event(eventName, parameters);
        console.log("커스텀 이벤트 추적:", eventName, parameters);
    } catch (error) {
        console.error("커스텀 이벤트 추적 실패:", error);
    }
};

/**
 * 상품 조회 이벤트 추적 (view_item)
 * @param {Object} itemData - 상품 데이터
 * @param {string} itemData.item_id - 상품 ID
 * @param {string} itemData.item_name - 상품명
 * @param {string} itemData.item_category - 상품 카테고리
 * @param {number} itemData.price - 가격
 * @param {string} itemData.currency - 통화 (기본: KRW)
 */
export const trackViewItem = ({
    item_id,
    item_name,
    item_category,
    price,
    currency = "KRW",
    ...customParams
}) => {
    try {
        ReactGA.event("view_item", {
            currency,
            value: price,
            items: [
                {
                    item_id,
                    item_name,
                    item_category,
                    price,
                },
            ],
            ...customParams,
        });
        console.log("상품 조회 이벤트 추적:", item_name);
    } catch (error) {
        console.error("상품 조회 이벤트 추적 실패:", error);
    }
};

/**
 * 장바구니 추가 이벤트 추적 (add_to_cart)
 * @param {Object} itemData - 상품 데이터
 */
export const trackAddToCart = ({
    item_id,
    item_name,
    item_category,
    price,
    quantity = 1,
    currency = "KRW",
    ...customParams
}) => {
    try {
        ReactGA.event("add_to_cart", {
            currency,
            value: price * quantity,
            items: [
                {
                    item_id,
                    item_name,
                    item_category,
                    price,
                    quantity,
                },
            ],
            ...customParams,
        });
        console.log("장바구니 추가 이벤트 추적:", item_name);
    } catch (error) {
        console.error("장바구니 추가 이벤트 추적 실패:", error);
    }
};

/**
 * 결제 시작 이벤트 추적 (begin_checkout)
 * @param {Array} items - 결제할 상품 목록
 * @param {number} value - 총 결제 금액
 */
export const trackBeginCheckout = ({ items, value, currency = "KRW" }) => {
    try {
        ReactGA.event("begin_checkout", {
            currency,
            value,
            items,
        });
        console.log("결제 시작 이벤트 추적:", value);
    } catch (error) {
        console.error("결제 시작 이벤트 추적 실패:", error);
    }
};

/**
 * 구매 완료 이벤트 추적 (purchase)
 * @param {Object} purchaseData - 구매 데이터
 * @param {string} purchaseData.transaction_id - 주문 ID
 * @param {number} purchaseData.value - 구매 금액
 * @param {Array} purchaseData.items - 구매 상품 목록
 */
export const trackPurchase = ({
    transaction_id,
    value,
    items,
    currency = "KRW",
    tax = 0,
    shipping = 0,
    ...customParams
}) => {
    try {
        ReactGA.event("purchase", {
            transaction_id,
            currency,
            value,
            tax,
            shipping,
            items,
            ...customParams,
        });
        console.log("구매 완료 이벤트 추적:", transaction_id);
    } catch (error) {
        console.error("구매 완료 이벤트 추적 실패:", error);
    }
};

/**
 * 회원가입 완료 이벤트 추적 (sign_up)
 * @param {string} method - 가입 방법 (예: 'email', 'kakao', 'naver')
 */
export const trackSignUp = (method = "email") => {
    try {
        ReactGA.event("sign_up", {
            method,
        });
        console.log("회원가입 완료 이벤트 추적:", method);
    } catch (error) {
        console.error("회원가입 완료 이벤트 추적 실패:", error);
    }
};

/**
 * 로그인 이벤트 추적 (login)
 * @param {string} method - 로그인 방법 (예: 'email', 'kakao', 'naver')
 */
export const trackLogin = (method = "email") => {
    try {
        ReactGA.event("login", {
            method,
        });
        console.log("로그인 이벤트 추적:", method);
    } catch (error) {
        console.error("로그인 이벤트 추적 실패:", error);
    }
};

/**
 * 예약 완료 이벤트 추적 (커스텀 이벤트)
 * @param {Object} reservationData - 예약 데이터
 */
export const trackReservation = ({
    reservation_id,
    reservation_type, // 'class', 'tour', 'practice'
    item_name,
    value,
    currency = "KRW",
    ...customParams
}) => {
    try {
        ReactGA.event("reservation_complete", {
            reservation_id,
            reservation_type,
            item_name,
            currency,
            value,
            ...customParams,
        });
        console.log("예약 완료 이벤트 추적:", reservation_id);
    } catch (error) {
        console.error("예약 완료 이벤트 추적 실패:", error);
    }
};
